import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ShoppingCart, Check } from 'lucide-react';
import { fetchProductsLight } from '../services/products';
import { useCart } from '../context/CartContext';

const VARUOSAD_CLASSES = ['decks', 'bars', 'wheels', 'forks', 'clamps', 'bar-ends', 'pegs', 'grips', 'griptape'];

interface UpsellItem {
  id: string;
  name: string;
  price: number;
  image: string;
  stock: number;
}

export default function UpsellSlider() {
  const [items, setItems] = useState<UpsellItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);
  const { addItem } = useCart();

  useEffect(() => {
    fetchProductsLight()
      .then((products: any[]) => {
        const upsellItems: UpsellItem[] = [];
        products
          .filter(p => VARUOSAD_CLASSES.includes(p.class))
          .forEach(p => {
            p.variants.forEach((v: any) => {
              if (v.stock > 0 && v.images?.length > 0) {
                upsellItems.push({
                  id: v.id,
                  name: v.name,
                  price: v.discounted_price ?? v.price,
                  image: v.images[0],
                  stock: v.stock,
                });
              }
            });
          });
        // Sort cheapest first — ideal for impulse buys
        upsellItems.sort((a, b) => a.price - b.price);
        setItems(upsellItems);
      })
      .catch(() => {/* silently fail */})
      .finally(() => setLoading(false));
  }, []);

  const updateScrollButtons = () => {
    const el = sliderRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    updateScrollButtons();
    return () => el.removeEventListener('scroll', updateScrollButtons);
  }, [items]);

  const scroll = (dir: 'left' | 'right') => {
    sliderRef.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  const handleAdd = (item: UpsellItem) => {
    addItem({ id: item.id, name: item.name, price: item.price, quantity: 1, image: item.image, stock: item.stock });
    setAddedIds(prev => new Set(prev).add(item.id));
    setTimeout(() => {
      setAddedIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }, 1600);
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="mt-10">
      <h2 className="text-xl font-bold mb-4">Kas miskit veel?</h2>
      <div className="relative">
        {/* Left arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white shadow-md rounded-full p-1.5 hover:bg-gray-50 hidden md:flex items-center justify-center"
            aria-label="Kerige vasakule"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
        )}

        {/* Slider */}
        <div
          ref={sliderRef}
          className="flex gap-4 overflow-x-auto pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {items.map(item => {
            const added = addedIds.has(item.id);
            return (
              <div
                key={item.id}
                className="flex-shrink-0 w-52 bg-white rounded-xl shadow-md overflow-hidden"
              >
                <div className="w-full h-44 bg-gray-100 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-3">
                  <p className="text-xs font-medium text-gray-800 line-clamp-2 mb-1 min-h-[2.5rem]">
                    {item.name}
                  </p>
                  <p className="text-sm font-bold text-[#4FC1E9] mb-2">{item.price.toFixed(2)}€</p>
                  <button
                    onClick={() => handleAdd(item)}
                    className={`w-full text-xs font-medium py-1.5 rounded-full flex items-center justify-center gap-1.5 transition-all duration-300 ${
                      added
                        ? 'bg-green-500 text-white'
                        : 'bg-[#4FC1E9] hover:bg-[#3ba8d0] text-white'
                    }`}
                  >
                    {added ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Lisatud!
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-3.5 h-3.5" />
                        Lisa korvi
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right arrow */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white shadow-md rounded-full p-1.5 hover:bg-gray-50 hidden md:flex items-center justify-center"
            aria-label="Kerige paremale"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>
    </div>
  );
}
